import React, { ReactNode, createContext, useCallback, useContext, useRef, useState } from 'react';
import { Page } from '@qualifio/api-client';
import { ProfileAttributes } from '@loyalty/shared-common/profile-api-client';
import { IsLoading, useLoaders } from '../shared/context/context-utils';
import { useAccounts } from '../accounts/AccountsContext';
import { useApi } from '../shared/api-client/ApiClientProvider';

export const profilesPageSize = 10;

export interface TestUserSearchParams {
  search?: string;
  pageNumber?: number;
}

export interface TestUsersContext {
  testUsers: Page<ProfileAttributes>;
  setTestUsers: (profiles: Page<ProfileAttributes>) => void; // use array instead of page ?
  selectedTestUser: ProfileAttributes | undefined;
  setSelectedTestUser: (profiles: ProfileAttributes | undefined) => void;
  currentPage: number;
  setCurrentPage: (pageNumber: number) => void;
  error: any;
  fetchTestUsers: (options?: TestUserSearchParams) => Promise<Page<ProfileAttributes> | undefined>;
  goToPreviousPage: () => void;
  goToNextPage: () => void;
  isLoading: IsLoading;
  deleteTestUser: (profile: ProfileAttributes) => Promise<void>;
  setCreationPopupIsOpen: (open: boolean) => void;
  creationPopupIsOpen: boolean;
}

export const TestUsersContext = createContext<TestUsersContext>({} as any);

export function useTestUsers() {
  return useContext(TestUsersContext);
}

export function TestUsersProvider({ children }: { children: ReactNode }) {
  const { currentAccount } = useAccounts();
  const { profileApi } = useApi();
  const [error, setError] = useState<any>();
  const [selectedTestUser, setSelectedTestUser] = useState<ProfileAttributes>();
  const [testUsers, setTestUsers] = useState<Page<ProfileAttributes>>(undefined as any);

  const [currentPage, setCurrentPage] = useState(0);
  const [creationPopupIsOpen, setCreationPopupIsOpen] = useState(false);
  const searchRef = useRef<string>();

  const { addLoader, removeLoader, isLoading } = useLoaders();

  const fetchTestUsers = useCallback(
    async ({ search, pageNumber }: TestUserSearchParams = {}) => {
      addLoader('searchProfiles');
      searchRef.current = search;

      if (!search || search.length > 2 || search.length === 0) {
        return profileApi
          .searchProfiles({
            params: {
              pageSize: profilesPageSize,
              search,
              pageNumber,
              accountId: currentAccount.id,
              isTestUser: true,
            },
          })
          .then(page => {
            setTestUsers(page);
            setError(undefined);
            return page;
          })
          .catch(error => {
            console.error(error);
            setError(error);
            return undefined;
          })
          .finally(() => removeLoader('searchProfiles'));
      }
    },
    [addLoader, currentAccount.id, profileApi, removeLoader],
  );

  const goToPreviousPage = async () => {
    const profiles = await fetchTestUsers({
      search: searchRef.current,
      pageNumber: currentPage - 1,
    });
    if (profiles) {
      setCurrentPage(prev => prev - 1);
      setSelectedTestUser(profiles.data[profiles.data.length - 1]);
    }
  };

  const goToNextPage = async () => {
    const profiles = await fetchTestUsers({
      search: searchRef.current,
      pageNumber: currentPage + 1,
    });
    if (profiles) {
      setCurrentPage(prev => prev + 1);
      setSelectedTestUser(profiles.data[0]);
    }
  };

  const deleteTestUser = (profile: ProfileAttributes) => {
    if (!(profile as any).isNew) {
      addLoader(deleteTestUser);
      return profileApi
        .deleteProfile(profile.id, { params: { forceDelete: true } })
        .then(async () => {
          setSelectedTestUser(undefined);
          const pageNumber =
            testUsers.isLast && testUsers.data.length === 1 && testUsers.previousPage !== undefined
              ? testUsers.previousPage
              : currentPage;
          await fetchTestUsers({ search: searchRef.current, pageNumber });
        })
        .catch(error => {
          console.error('Could not delete test user ', error);
        })
        .finally(() => {
          removeLoader(deleteTestUser);
        });
    } else {
      setTestUsers(() => {
        return {
          ...testUsers,
          data: testUsers.data.filter(user => user.id !== profile.id),
        };
      });
      setSelectedTestUser(undefined);
      return Promise.resolve();
    }
  };

  const context = {
    testUsers,
    setTestUsers,
    selectedTestUser,
    setSelectedTestUser,
    currentPage,
    setCurrentPage,
    error,
    fetchTestUsers,
    goToPreviousPage,
    goToNextPage,
    isLoading,
    deleteTestUser,
    setCreationPopupIsOpen,
    creationPopupIsOpen,
  };

  return <TestUsersContext.Provider value={context}>{children}</TestUsersContext.Provider>;
}
